import React, { useState, useEffect } from "react";
import { X, Send, Star, MessageCircle, Lightbulb, AlertCircle, Mic } from "lucide-react";
import VoiceDetectionDialog from "./VoiceDetectionDialog";

const SuggestionModal = ({ onClose }) => {
  const [inputMethod, setInputMethod] = useState(""); // "", "form", "voice"
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    employee_id: "",
    category: "",
    subject: "",
    message: "",
    rating: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showVoiceDialog, setShowVoiceDialog] = useState(false);
  const [activeField, setActiveField] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const categories = [
    { value: "system", label: "Системийн асуудал" },
    { value: "service", label: "Үйлчилгээний сайжруулалт" },
    { value: "feature", label: "Шинэ функц хүсэлт" },
    { value: "bug", label: "Алдаа мэдэгдэх" },
    { value: "other", label: "Бусад" }
  ];

  // Fetch employees when component mounts
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const response = await fetch(window.location.origin + '/get-employees.php');
        const result = await response.json();

        if (result.success && result.data) {
          setEmployees(result.data);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, []);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Handle click outside modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Нэр заавал бөглөнө үү";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "И-мэйл заавал бөглөнө үү";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "И-мэйл хаягийн форматыг зөв оруулна уу";
    }

    if (!formData.employee_id) {
      newErrors.employee_id = "Ажилтан сонгоно уу";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Гарчиг заавал бөглөнө үү";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Дэлгэрэнгүй агуулга заавал бөглөнө үү";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Дэлгэрэнгүй агуулга хамгийн багадаа 10 тэмдэгт байх ёстой";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // For voice method, we need basic validation
    if (inputMethod === "voice") {
      if (!formData.message.trim()) {
        setErrorMessage("Санал хүсэлт хоосон байна. Дахин оролдоно уу.");
        setShowError(true);
        return;
      }
    } else {
      // For form method, validate all fields
      if (!validateForm()) {
        return;
      }
    }

    setIsSubmitting(true);
    setShowError(false);
    
    try {
      // Prepare data for submission
      let submissionData;

      if (inputMethod === "voice") {
        // Voice submission - only send message and inputMethod
        submissionData = {
          message: formData.message,
          inputMethod: "voice"
        };
      } else {
        // Form submission - send all data
        submissionData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          employee_id: formData.employee_id,
          subject: formData.subject,
          message: formData.message,
          inputMethod: "form"
        };
      }

      // PHP API URL - Update with your actual cPanel domain
      const apiUrl = window.location.origin + '/suggestions.php'; // Auto-detects current domain

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      console.log('API Response:', result); // Debug log

      if (response.ok && result.success) {
        setShowSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const errorMsg = result.message || result.error || result.detail?.message || 'Submission failed';
        throw new Error(errorMsg);
      }

    } catch (error) {
      console.error("Submission error:", error);
      // Show the actual error message from the server
      setErrorMessage(error.message || "Алдаа гарлаа. Дахин оролдоно уу.");
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  // Handle rating
  const handleRating = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  // Handle voice input
  const handleVoiceInput = (field) => {
    setActiveField(field);
    setShowVoiceDialog(true);
  };

  // Handle voice result
  const handleVoiceResult = (transcript) => {
    if (inputMethod === "voice") {
      setFormData(prev => ({
        ...prev,
        message: transcript
      }));
      setShowVoiceDialog(false);
    } else if (activeField && transcript) {
      setFormData(prev => ({
        ...prev,
        [activeField]: transcript
      }));
      
      // Clear error for this field
      if (errors[activeField]) {
        setErrors(prev => ({
          ...prev,
          [activeField]: ""
        }));
      }
      setShowVoiceDialog(false);
      setActiveField("");
    }
  };

  // Handle going back to method selection
  const handleBack = () => {
    setInputMethod("");
    setFormData({
      name: "",
      email: "",
      phone: "",
      employee_id: "",
      category: "",
      subject: "",
      message: "",
      rating: 0
    });
    setErrors({});
    setShowError(false);
  };

  // Success screen
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleBackdropClick}>
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center transform animate-scale-in">
          <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Амжилттай илгээгдлээ!</h3>
          <p className="text-gray-600">Таны санал хүсэлтийг хүлээн авлаа. Удахгүй хариулт илгээх болно.</p>
        </div>
      </div>
    );
  }

  // Method selection screen
  if (!inputMethod) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={handleBackdropClick}>
        <div className="bg-white rounded-2xl max-w-md w-full transform animate-scale-in shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center hover:bg-opacity-30 transition-colors"
            >
              <X size={16} />
            </button>
            <h2 className="text-xl font-semibold mb-1">Санал хүсэлт өгөх</h2>
            <p className="text-blue-100 text-sm">Оруулах аргаа сонгоно уу</p>
          </div>

          {/* Method Selection */}
          <div className="p-6 space-y-4">
            <div className="text-center mb-6">
              <p className="text-gray-600">Та санал хүсэлтээ хэрхэн оруулахыг хүсч байна вэ?</p>
            </div>

            {/* Form Input Option */}
            <button
              onClick={() => setInputMethod("form")}
              className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Маягт бөглөх</h3>
                  <p className="text-sm text-gray-600">Дэлгэрэнгүй мэдээллийг маягтаар оруулах</p>
                </div>
              </div>
            </button>

            {/* Voice Input Option */}
            <button
              onClick={() => {
                setInputMethod("voice");
                setShowVoiceDialog(true);
              }}
              className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all duration-200 group"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <Mic className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">Дуугаар оруулах</h3>
                  <p className="text-sm text-gray-600">Санал хүсэлтээ дуугаар хэлэх</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Voice input screen
  if (inputMethod === "voice") {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={handleBackdropClick}>
        <div className="bg-white rounded-2xl max-w-lg w-full transform animate-scale-in shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-2xl relative">
            <button
              onClick={handleBack}
              className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center hover:bg-opacity-30 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center hover:bg-opacity-30 transition-colors"
            >
              <X size={16} />
            </button>
            <h2 className="text-xl font-semibold mb-1 text-center">Дуугаар санал хүсэлт</h2>
            <p className="text-green-100 text-sm text-center">Та санал хүсэлтээ хэлнэ үү</p>
          </div>

          {/* Voice Content */}
          <div className="p-6">
            {showError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{errorMessage}</p>
              </div>
            )}

            {formData.message ? (
              <div className="space-y-4">
                <div className="bg-gray-50 border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Таны санал хүсэлт:
                  </label>
                  <p className="text-gray-900">{formData.message}</p>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowVoiceDialog(true)}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Mic size={18} />
                    <span>Дахин хэлэх</span>
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Илгээж байна...</span>
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        <span>Илгээх</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Mic size={40} className="text-green-600" />
                </div>
                <p className="text-gray-600 mb-4">Санал хүсэлтээ хэлэхийн тулд доорх товчийг дарна уу</p>
                <button
                  onClick={() => setShowVoiceDialog(true)}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center space-x-2 mx-auto"
                >
                  <Mic size={18} />
                  <span>Дуу бичлэг эхлүүлэх</span>
                </button>
              </div>
            )}
          </div>

          {/* Voice Dialog */}
          {showVoiceDialog && (
            <VoiceDetectionDialog
              setShowVoiceDialog={setShowVoiceDialog}
              onResult={handleVoiceResult}
              navigateToTemplate={false}
            />
          )}
        </div>
      </div>
    );
  }

  // Form input screen
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={handleBackdropClick}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto transform animate-scale-in shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl relative">
          <button
            onClick={handleBack}
            className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center hover:bg-opacity-30 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center hover:bg-opacity-30 transition-colors"
          >
            <X size={16} />
          </button>
          <h2 className="text-xl font-semibold mb-1 text-center">Маягт бөглөх</h2>
          <p className="text-blue-100 text-sm text-center">Дэлгэрэнгүй мэдээллээ оруулна уу</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {showError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Нэр <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Таны нэрийг оруулна уу"
              />
              <button
                type="button"
                onClick={() => handleVoiceInput("name")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-blue-600 transition-colors"
              >
                <Mic size={18} />
              </button>
            </div>
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              И-мэйл хаяг <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 ${
                errors.email ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="example@email.com"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Утасны дугаар
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              placeholder="99123456"
            />
          </div>

          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ажилтан <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.employee_id}
              onChange={(e) => handleInputChange("employee_id", e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 ${
                errors.employee_id ? "border-red-500" : "border-gray-300"
              }`}
              disabled={loadingEmployees}
            >
              <option value="">
                {loadingEmployees ? "Ачааллаж байна..." : "Ажилтан сонгоно уу"}
              </option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} {employee.position ? `- ${employee.position}` : ''}
                </option>
              ))}
            </select>
            {errors.employee_id && <p className="text-red-500 text-xs mt-1">{errors.employee_id}</p>}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Гарчиг <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange("subject", e.target.value)}
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 ${
                  errors.subject ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Санал хүсэлтийн товч агуулга"
              />
              <button
                type="button"
                onClick={() => handleVoiceInput("subject")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-blue-600 transition-colors"
              >
                <Mic size={18} />
              </button>
            </div>
            {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Дэлгэрэнгүй агуулга <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <textarea
                value={formData.message}
                onChange={(e) => handleInputChange("message", e.target.value)}
                rows={4}
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-gray-50 ${
                  errors.message ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Та санал хүсэлтээ энд дэлгэрэнгүй бичнэ үү..."
              />
              <button
                type="button"
                onClick={() => handleVoiceInput("message")}
                className="absolute right-3 top-3 p-1 text-gray-400 hover:text-blue-600 transition-colors"
              >
                <Mic size={18} />
              </button>
            </div>
            {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed font-medium"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Илгээж байна...</span>
                </>
              ) : (
                <>
                  <Send size={18} />
                  <span>Илгээх</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Voice Dialog */}
        {showVoiceDialog && (
          <VoiceDetectionDialog
            setShowVoiceDialog={setShowVoiceDialog}
            onResult={handleVoiceResult}
            navigateToTemplate={false}
          />
        )}
      </div>
    </div>
  );
};

export default SuggestionModal;